import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { X, Loader2, ClipboardPaste, Upload, FileArchive } from 'lucide-react';
import { generateSampleData, processUploadedFiles } from '../utils/helpers';

const ImportModal = ({ isOpen, onClose, onImport, onImportFiles, isLoading }) => {
  const [pastedData, setPastedData] = useState(null);
  const [selectedFiles, setSelectedFiles] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const pasteAreaRef = useRef(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (isOpen && pasteAreaRef.current && !selectedFiles) {
      pasteAreaRef.current.focus();
    }
  }, [isOpen, selectedFiles]);

  const handlePaste = async (e) => {
    e.preventDefault();
    const text = e.clipboardData.getData('text');

    if (text.trim()) {
      const lineCount = text.split(/\r?\n/).length;
      const charCount = text.length;
      setPastedData({ text, lineCount, charCount });
      await onImport(text);
      setPastedData(null);
      onClose();
    }
  };

  const handleLoadSample = () => {
    const text = generateSampleData();
    const lineCount = text.split('\n').length;
    const charCount = text.length;
    setPastedData({ text, lineCount, charCount });
  };

  const handleImport = async () => {
    if (pastedData?.text) {
      await onImport(pastedData.text);
      setPastedData(null);
    }
  };

  const handleFilesSelected = async (files) => {
    if (!files || files.length === 0) return;

    try {
      const processedFiles = await processUploadedFiles(Array.from(files));
      
      if (processedFiles.length > 0) {
        const totalLines = processedFiles.reduce((sum, f) => sum + f.lineCount, 0);
        setSelectedFiles({
          files: processedFiles,
          fileCount: processedFiles.length,
          totalLines
        });
      }
    } catch (error) {
      console.error('Error processing files:', error);
      alert('Error processing files. Please ensure they are valid log files.');
    }
  };

  const handleFileInputChange = (e) => {
    handleFilesSelected(e.target.files);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    handleFilesSelected(e.dataTransfer.files);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleImportFiles = async () => {
    if (selectedFiles?.files && onImportFiles) {
      await onImportFiles(selectedFiles.files);
      setSelectedFiles(null);
      onClose();
    }
  };

  const handleClose = () => {
    setPastedData(null);
    setSelectedFiles(null);
    onClose();
  };

  const handleClear = () => {
    setPastedData(null);
    setSelectedFiles(null);
    if (pasteAreaRef.current) {
      pasteAreaRef.current.focus();
    }
  };

  const handleBrowseFiles = () => {
    fileInputRef.current?.click();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 backdrop-blur-sm">
      <div className="bg-slate-800 rounded-lg shadow-2xl w-3/4 max-w-4xl flex flex-col max-h-[90vh] border border-slate-700">
        <div className="p-4 border-b border-slate-700 flex justify-between items-center">
          <h2 className="text-xl font-semibold text-white">
            {isLoading ? 'Processing Logs...' : 'Import Logs'}
          </h2>
          <button
            onClick={handleClose}
            className="text-slate-400 hover:text-white"
            disabled={isLoading}
          >
            <X size={24} />
          </button>
        </div>
        {isLoading ? (
          <div className="p-12 flex-1 flex flex-col items-center justify-center gap-4">
            <Loader2 size={48} className="text-blue-500 animate-spin" />
            <p className="text-slate-300 text-lg">Parsing log entries...</p>
            <p className="text-slate-500 text-sm">This may take a moment for large files</p>
          </div>
        ) : (
          <>
            <div className="p-8 flex-1 flex flex-col items-center justify-center min-h-0">
              {!pastedData && !selectedFiles ? (
                <div className="w-full h-full flex flex-col gap-4">
                  {/* File Upload Area */}
                  <div
                    onDrop={handleDrop}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    className={`flex-1 flex flex-col items-center justify-center gap-6 border-2 border-dashed rounded-lg transition-all ${
                      isDragging
                        ? 'border-blue-500 bg-blue-500/10'
                        : 'border-slate-600 bg-slate-900/50 hover:border-blue-500 hover:bg-slate-900'
                    }`}
                  >
                    <input
                      ref={fileInputRef}
                      type="file"
                      multiple
                      accept=".log,.zip"
                      onChange={handleFileInputChange}
                      className="hidden"
                    />
                    <div className="flex gap-4">
                      <FileArchive size={64} className="text-slate-600" />
                      <Upload size={64} className="text-slate-600" />
                    </div>
                    <div className="text-center">
                      <p className="text-xl text-slate-300 font-medium mb-2">
                        Drop files here or click to browse
                      </p>
                      <p className="text-sm text-slate-500 mb-4">
                        Supports .log files and .zip archives
                      </p>
                      <button
                        onClick={handleBrowseFiles}
                        className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded font-medium"
                      >
                        Browse Files
                      </button>
                    </div>
                    <div className="text-xs text-slate-600 text-center max-w-md">
                      <p className="mb-1">Supported formats:</p>
                      <p className="font-mono">&lt;timestamp&gt; &lt;message&gt;</p>
                      <p className="font-mono">&lt;container&gt; | &lt;timestamp&gt; &lt;message&gt;</p>
                    </div>
                  </div>

                  {/* Paste Area */}
                  <div
                    ref={pasteAreaRef}
                    tabIndex={0}
                    onPaste={handlePaste}
                    className="flex-1 flex flex-col items-center justify-center gap-4 border-2 border-dashed border-slate-600 rounded-lg bg-slate-900/50 hover:border-blue-500 hover:bg-slate-900 transition-all cursor-text focus:outline-none focus:ring-2 focus:ring-blue-600 focus:border-blue-600"
                  >
                    <ClipboardPaste size={48} className="text-slate-600" />
                    <div className="text-center">
                      <p className="text-lg text-slate-300 font-medium mb-1">
                        Or paste logs here
                      </p>
                      <p className="text-xs text-slate-500">
                        Click here and press Ctrl+V (Cmd+V on Mac)
                      </p>
                    </div>
                  </div>
                </div>
              ) : selectedFiles ? (
                <div className="w-full h-full flex flex-col items-center justify-center gap-6 border-2 border-green-600/50 rounded-lg bg-slate-900/50 p-8">
                  <div className="text-center w-full">
                    <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-600/20 mb-4">
                      <FileArchive size={32} className="text-green-500" />
                    </div>
                    <p className="text-2xl text-slate-300 font-medium mb-2">
                      Ready to analyze
                    </p>
                    <div className="flex gap-6 justify-center text-slate-400 mt-4 mb-6">
                      <div className="text-center">
                        <p className="text-3xl font-bold text-blue-400">
                          {selectedFiles.fileCount}
                        </p>
                        <p className="text-sm text-slate-500">files</p>
                      </div>
                      <div className="text-center">
                        <p className="text-3xl font-bold text-blue-400">
                          {selectedFiles.totalLines.toLocaleString()}
                        </p>
                        <p className="text-sm text-slate-500">log lines</p>
                      </div>
                    </div>
                    <div className="max-h-48 overflow-y-auto mb-4 bg-slate-900 rounded p-4 text-left">
                      <p className="text-xs text-slate-400 mb-2">Files to import:</p>
                      {selectedFiles.files.map((file, idx) => (
                        <div key={idx} className="text-sm text-slate-300 font-mono mb-1">
                          📄 {file.filename} ({file.lineCount} lines)
                        </div>
                      ))}
                    </div>
                  </div>
                  <button
                    onClick={handleClear}
                    className="text-sm text-slate-500 hover:text-slate-300 underline"
                  >
                    Clear and select different files
                  </button>
                </div>
              ) : (
                <div className="w-full h-full flex flex-col items-center justify-center gap-6 border-2 border-green-600/50 rounded-lg bg-slate-900/50 p-8">
                  <div className="text-center">
                    <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-600/20 mb-4">
                      <ClipboardPaste size={32} className="text-green-500" />
                    </div>
                    <p className="text-2xl text-slate-300 font-medium mb-2">
                      Ready to analyze
                    </p>
                    <div className="flex gap-6 justify-center text-slate-400 mt-4">
                      <div className="text-center">
                        <p className="text-3xl font-bold text-blue-400">
                          {pastedData.lineCount.toLocaleString()}
                        </p>
                        <p className="text-sm text-slate-500">lines</p>
                      </div>
                      <div className="text-center">
                        <p className="text-3xl font-bold text-blue-400">
                          {(pastedData.charCount / 1024).toFixed(1)}
                        </p>
                        <p className="text-sm text-slate-500">KB</p>
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={handleClear}
                    className="text-sm text-slate-500 hover:text-slate-300 underline"
                  >
                    Clear and paste again
                  </button>
                </div>
              )}
            </div>
            <div className="p-4 border-t border-slate-700 flex justify-end gap-3">
              <button
                onClick={handleLoadSample}
                className="text-slate-400 hover:text-white text-sm mr-auto"
              >
                Load Sample Data
              </button>
              <button
                onClick={handleClose}
                className="px-4 py-2 text-slate-300 hover:bg-slate-700 rounded"
              >
                Cancel
              </button>
              <button
                onClick={selectedFiles ? handleImportFiles : handleImport}
                className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded font-medium disabled:bg-slate-700 disabled:text-slate-500 disabled:cursor-not-allowed"
                disabled={!pastedData && !selectedFiles}
              >
                Analyze Logs
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

ImportModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onImport: PropTypes.func.isRequired,
  onImportFiles: PropTypes.func,
  isLoading: PropTypes.bool.isRequired
};

export default ImportModal;

